import { useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@context/AuthContext';
import { getTeamQueues, answerQueueCall } from '@services/api';
import { parseApiError } from '@utils/errorUtils';
import OutgoingCall from '@components/CallControls/OutgoingCall';
import './PhoneControlsTab.css';

export default function PhoneControlsTab({
  device,
  isReady,
  isLoading,
  error,
  activeCall,
  callStatus,
  onCallStarted,
  onCallEnded,
  onCallConnectionReady,
  selectedQueue,
  onQueueSelected,
  defaultTestPatientGuid,
}) {
  return (
    <div className="phone-controls-tab">
      {error && (
        <div className="alert alert-error">
          Failed to initialize Twilio Device. Please refresh the page.
        </div>
      )}

      <div className="controls-grid">
        <div className="controls-grid-left">
          <OutgoingCall
            device={device}
            isReady={isReady}
            onCallStarted={onCallStarted}
            onCallEnded={onCallEnded}
            onCallConnectionReady={onCallConnectionReady}
            defaultPatientGuid={defaultTestPatientGuid}
          />

          {callStatus !== 'idle' && (
            <div className="call-status-card">
              <h3>Call Status</h3>
              <p>Status: <strong>{callStatus.toUpperCase()}</strong></p>
              {selectedQueue && <p>Queue: <strong>{selectedQueue}</strong></p>}
            </div>
          )}
        </div>

        <div className="controls-grid-right">
          <QueueSection />
        </div>
      </div>
    </div>
  );
}

function QueueSection() {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [answerResult, setAnswerResult] = useState(null);
  const [answerError, setAnswerError] = useState('');

  const {
    data: teams = [],
    isLoading,
    error: loadError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['teamQueues'],
    queryFn: getTeamQueues,
    refetchInterval: 10_000,
    retry: 2,
  });

  useEffect(() => {
    if (teams.length === 0) return;
    if (selectedTeamId != null && teams.some(t => t.teamId === selectedTeamId)) return;

    const myTeam = teams.find(t => t.teamId === user?.teamId)
      ?? teams.find(t => t.teamName === user?.teamName)
      ?? null;
    setSelectedTeamId(myTeam ? myTeam.teamId : teams[0].teamId);
  }, [teams, user?.teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const answerMutation = useMutation({
    mutationFn: answerQueueCall,
    onSuccess: (data, variables) => {
      setAnswerResult({ queueName: variables.queueName, callSid: data.parentCallSid });
      setAnswerError('');
      queryClient.invalidateQueries({ queryKey: ['teamQueues'] });
    },
    onError: (err) => {
      setAnswerError(
        err.response?.status === 404
          ? 'No callers in queue right now.'
          : `Failed to answer: ${parseApiError(err)}`
      );
      setAnswerResult(null);
    },
  });

  const handleAnswer = (queueName) => {
    setAnswerResult(null);
    setAnswerError('');
    answerMutation.mutate({ queueName });
  };

  const selectedTeam = teams.find(t => t.teamId === selectedTeamId) ?? null;
  const totalCallers = teams.reduce((sum, t) => sum + t.queuedCallers, 0);

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    : null;

  return (
    <section className="queue-section">
      <div className="queue-header">
        <h2>Queue</h2>
        {totalCallers > 0 && (
          <div className="queue-summary-badge">
            <span className="queue-summary-number">{totalCallers}</span>
            <span className="queue-summary-label">
              {totalCallers === 1 ? 'waiting' : 'waiting'}
            </span>
          </div>
        )}
      </div>

      {lastUpdated && (
        <p className="queue-updated">Last updated: {lastUpdated}</p>
      )}

      {answerResult && (
        <div className="queue-answer-success">
          <span className="queue-success-icon">&#10003;</span>
          <span>
            Connecting to a caller from <strong>{answerResult.queueName}</strong> — your device will ring momentarily.
          </span>
          <button className="queue-dismiss-btn" onClick={() => setAnswerResult(null)}>&#215;</button>
        </div>
      )}

      {answerError && (
        <div className="queue-answer-error">
          <span>{answerError}</span>
          <button className="queue-dismiss-btn" onClick={() => setAnswerError('')}>&#215;</button>
        </div>
      )}

      {isLoading && (
        <div className="queue-loading">
          <div className="queue-loading-spinner" />
          Loading queue...
        </div>
      )}

      {loadError && !isLoading && (
        <div className="queue-load-error">
          Failed to load queue: {parseApiError(loadError)}
        </div>
      )}

      {!isLoading && !loadError && teams.length === 0 && (
        <div className="queue-empty">No queue assigned to your team.</div>
      )}

      {!isLoading && teams.length > 0 && (
        <>
          <div className="queue-team-selector-row">
            <label className="queue-team-selector-label" htmlFor="queue-team-select">Queue</label>
            <select
              id="queue-team-select"
              className="queue-team-select"
              value={selectedTeamId ?? ''}
              onChange={e => setSelectedTeamId(Number(e.target.value))}
            >
              {teams.map(t => (
                <option key={t.teamId} value={t.teamId}>
                  {t.teamName}{t.queuedCallers > 0 ? ` (${t.queuedCallers})` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedTeam && (
            <div className="queue-panel">
              <div className="queue-panel-main">
                <div className="queue-panel-count">
                  <span className={`queue-caller-count ${selectedTeam.queuedCallers > 0 ? 'active' : 'zero'}`}>
                    {selectedTeam.queuedCallers}
                  </span>
                  <span className="queue-caller-label">
                    {selectedTeam.queuedCallers === 1 ? 'caller waiting' : 'callers waiting'}
                  </span>
                  {selectedTeam.queuedCallers > 0 && <span className="queue-pulse" />}
                </div>

                <button
                  className={`queue-answer-btn ${selectedTeam.queuedCallers > 0 ? 'ready' : 'disabled'}`}
                  onClick={() => handleAnswer(selectedTeam.teamName)}
                  disabled={selectedTeam.queuedCallers === 0 || answerMutation.isPending}
                >
                  {answerMutation.isPending ? (
                    <><span className="queue-btn-spinner" />Connecting...</>
                  ) : (
                    <>Answer</>
                  )}
                </button>
              </div>

              {selectedTeam.totalCallers === 0 && (
                <p className="queue-empty-note">Queue is empty.</p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
